import React from 'react'

import Stack from '@mui/material/Stack'

import { subMonths } from 'date-fns'

import { observer } from 'mobx-react-lite'

import {
  ANALYTICS_EVENTS,
  Loader,
  MicroWidgetContainer,
  useAccountStore,
  useCategoryStore,
  useEvent,
  useGlobalCopyStore,
  useGlobalUiStore,
  useWidgetLoadTimer,
} from '../common'

import { TrendsMicroSpendingIncomeBox } from './components/TrendsMicroSpendingIncomeBox'
import { calculateRelativeChange } from './utils/shared'
import { getIncomeTotals, getSpendingTotals } from './utils/SpendingData'
import { ZeroStateMicroWidget } from './ZeroStateMicroWidget'

interface TrendsMicroWidgetProps {
  onCtaClick: () => void
}
export const TrendsMicroWidget = observer(({ onCtaClick }: TrendsMicroWidgetProps) => {
  const { isAccountDataLoaded, loadAccountData, visibleAccounts: accounts } = useAccountStore()

  React.useEffect(() => {
    if (!isAccountDataLoaded) {
      loadAccountData().finally()
    }
  }, [])

  const { onEvent } = useEvent()
  const { monthlyCategoryTotals, loadMonthlyCategoryTotals, monthlyTotalsLoaded } =
    useCategoryStore()

  const dateRange = {
    start: subMonths(new Date(), 1),
    end: new Date(),
  }

  const { trends: trendsCopy } = useGlobalCopyStore()

  const { selectedAccounts, isInitialized } = useGlobalUiStore()

  useWidgetLoadTimer({
    widgetName: 'TrendsMicroWidget',
    isLoaded: monthlyTotalsLoaded,
  })

  React.useEffect(() => {
    if (isInitialized) {
      loadMonthlyCategoryTotals(selectedAccounts, dateRange.start).then(() => {
        onEvent(ANALYTICS_EVENTS.TRENDS_LOAD_WIDGET, {
          state: !accounts?.length ? 'zeroState' : 'default',
        })
      })
    }
  }, [isInitialized, selectedAccounts])

  const { spendingData, incomeData } = React.useMemo(
    () => ({
      spendingData: getSpendingTotals(monthlyCategoryTotals, dateRange),
      incomeData: getIncomeTotals(monthlyCategoryTotals, dateRange),
    }),
    [monthlyCategoryTotals, dateRange],
  )

  const [lastMonthSpending, thisMonthSpending] = spendingData
  const [lastMonthIncome, thisMonthIncome] = incomeData

  const spendingPercentage = calculateRelativeChange(thisMonthSpending.y, lastMonthSpending.y)
  const incomePercentage = calculateRelativeChange(thisMonthIncome.y, lastMonthIncome.y)

  const showZeroState = isInitialized && !accounts?.length

  const handleCtaClick = () => {
    onEvent(
      showZeroState
        ? ANALYTICS_EVENTS.TRENDS_CLICK_GET_STARTED
        : ANALYTICS_EVENTS.TRENDS_CLICK_VIEW_MORE,
    )
    onCtaClick()
  }

  if (!monthlyTotalsLoaded || !isInitialized) return <Loader />

  return (
    <MicroWidgetContainer
      className="mx-trends-microwidget"
      ctaLabel={trendsCopy.micro_primary_cta_label}
      onCTAClick={handleCtaClick}
      subHeader={!showZeroState ? trendsCopy.micro_subheader : undefined}
      title={trendsCopy.title}
    >
      {!showZeroState ? (
        <Stack
          sx={{
            alignItems: 'stretch',
            flexDirection: 'row',
            gap: 12,
          }}
        >
          <TrendsMicroSpendingIncomeBox
            percentage={spendingPercentage}
            secondaryLabel={trendsCopy.micro_secondary_label}
            shouldDisplayPercentage={thisMonthSpending.y !== 0 && lastMonthSpending.y !== 0}
            title={trendsCopy.spending_label}
            totalAmount={thisMonthSpending.y}
            transactionType={'spending'}
          />
          <TrendsMicroSpendingIncomeBox
            percentage={incomePercentage}
            secondaryLabel={trendsCopy.micro_secondary_label}
            shouldDisplayPercentage={thisMonthIncome.y !== 0 && lastMonthIncome.y !== 0}
            title={trendsCopy.income_label}
            totalAmount={thisMonthIncome.y}
            transactionType={'income'}
          />
        </Stack>
      ) : (
        <ZeroStateMicroWidget />
      )}
    </MicroWidgetContainer>
  )
})
